/**
 * ImpactBadge Component
 *
 * Displays impact summary with inline key metrics and expandable details.
 */

import { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import type { UnifiedImpactDetails } from "@a7y/types";

interface ImpactBadgeProps {
  summary: string;
  details?: UnifiedImpactDetails;
  className?: string;
}

export function ImpactBadge({ summary, details, className = "" }: ImpactBadgeProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasExpandableContent = details && (details.metric_type || details.source_note);

  return (
    <div className={`${className}`}>
      <button
        onClick={() => hasExpandableContent && setIsExpanded(!isExpanded)}
        className={`flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 ${hasExpandableContent ? "cursor-pointer hover:text-gray-800 dark:hover:text-gray-200" : "cursor-default"}`}
        disabled={!hasExpandableContent}
      >
        <span>{summary}</span>
        {details?.install_impact && (
          <span className="ml-2 text-xs text-green-600 dark:text-green-400 font-medium">
            {details.install_impact}
          </span>
        )}
        {details?.range && (
          <span className="ml-1 text-xs text-purple-600 dark:text-purple-400 font-medium">
            {details.range}
          </span>
        )}
        {hasExpandableContent && (
          isExpanded ? (
            <ChevronUpIcon className="size-4" />
          ) : (
            <ChevronDownIcon className="size-4" />
          )
        )}
      </button>

      {isExpanded && details && (
        <div className="mt-2 p-3 bg-gray-50 dark:bg-zinc-800 rounded-md text-sm space-y-1">
          {details.metric_type && (
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Metric:</span>
              <span className="font-medium">{details.metric_type}</span>
            </div>
          )}
          {details.source_note && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 italic">
              {details.source_note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
